import React, { useEffect, useState } from 'react';
import './Timestamp.scss';

/**
 * A point-in-time timestamp that cycles through three displays on click:
 * UTC (default) -> local time -> relative ("23 hours ago"). The mode is shared by every
 * Timestamp on the page and remembered for the session, so one click switches them all.
 * Formatting is the browser's Intl API; no date library is needed.
 */
export type TimestampMode = 'utc' | 'local' | 'relative';

const MODES: TimestampMode[] = ['utc', 'local', 'relative'];
const STORAGE_KEY = 'sherlock.timestampMode';
const MODE_LABEL: Record<TimestampMode, string> = { utc: 'UTC', local: 'local time', relative: 'relative time' };

// ---- shared mode store (module-level, session-persisted) ----
const listeners = new Set<() => void>();

const readStoredMode = (): TimestampMode => {
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        return MODES.includes(stored as TimestampMode) ? (stored as TimestampMode) : 'utc';
    } catch {
        return 'utc';
    }
};

let currentMode: TimestampMode = readStoredMode();

const setMode = (mode: TimestampMode): void => {
    currentMode = mode;
    try {
        window.sessionStorage.setItem(STORAGE_KEY, mode);
    } catch {
        // storage unavailable (private mode / disabled): mode still applies for this page load
    }
    listeners.forEach((l) => l());
};

/** Exposed for tests so each case starts from the UTC default. */
export const resetTimestampMode = (): void => setMode('utc');

const useTimestampMode = (): [TimestampMode, () => void] => {
    const [mode, setLocal] = useState<TimestampMode>(currentMode);
    useEffect(() => {
        const sync = (): void => setLocal(currentMode);
        listeners.add(sync);
        sync();
        return () => {
            listeners.delete(sync);
        };
    }, []);
    const cycle = (): void => setMode(MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]);
    return [mode, cycle];
};

// ---- formatting ----
const ABSOLUTE_OPTIONS: Intl.DateTimeFormatOptions = {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    timeZoneName: 'short',
};

/** "10/12/2026, 20:08:33" plus the zone the browser names ("UTC", "PDT", "GMT+5:30"). */
export const formatAbsolute = (date: Date, mode: 'utc' | 'local'): { text: string; zone: string } => {
    const parts = new Intl.DateTimeFormat(undefined, {
        ...ABSOLUTE_OPTIONS,
        timeZone: mode === 'utc' ? 'UTC' : undefined,
    }).formatToParts(date);
    const zone = parts.find((p) => p.type === 'timeZoneName')?.value ?? (mode === 'utc' ? 'UTC' : 'local');
    const text = parts
        .filter((p) => p.type !== 'timeZoneName')
        .map((p) => p.value)
        .join('')
        .trim()
        .replace(/[,\s]+$/, '');
    return { text, zone };
};

const RELATIVE_UNITS: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [365 * 86_400, 'year'],
    [30 * 86_400, 'month'],
    [86_400, 'day'],
    [3_600, 'hour'],
    [60, 'minute'],
];

/** "23 hours ago", "1 month ago", "in 2 days"; under a minute reads "just now". */
export const formatRelative = (date: Date, now: number = Date.now()): string => {
    const seconds = Math.round((date.getTime() - now) / 1000);
    if (Math.abs(seconds) < 60) {
        return 'just now';
    }
    const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'always' });
    for (const [unitSeconds, unit] of RELATIVE_UNITS) {
        if (Math.abs(seconds) >= unitSeconds) {
            return formatter.format(Math.trunc(seconds / unitSeconds), unit);
        }
    }
    return 'just now';
};

interface TimestampProps {
    /** ISO string or Date. Invalid or missing values render the fallback text. */
    date: string | Date | null | undefined;
    fallback?: string;
    className?: string;
}

const Timestamp: React.FC<TimestampProps> = ({ date, fallback = '—', className }) => {
    const [mode, cycle] = useTimestampMode();
    // Relative text drifts; re-render once a minute while it is showing.
    const [, tick] = useState(0);
    useEffect(() => {
        if (mode !== 'relative') {
            return undefined;
        }
        const id = window.setInterval(() => tick((n) => n + 1), 60_000);
        return () => window.clearInterval(id);
    }, [mode]);

    const parsed = date instanceof Date ? date : date ? new Date(date) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) {
        return <span className={className}>{fallback}</span>;
    }

    const { text, zone } = mode === 'relative'
        ? { text: formatRelative(parsed), zone: 'AGO' }
        : formatAbsolute(parsed, mode);
    const next = MODES[(MODES.indexOf(mode) + 1) % MODES.length];

    return (
        <button
            type="button"
            className={`timestamp timestamp--${mode}${className ? ` ${className}` : ''}`}
            onClick={cycle}
            title={`${parsed.toISOString()} — showing ${MODE_LABEL[mode]}; click for ${MODE_LABEL[next]}`}
            aria-label={`${text} ${zone}. Showing ${MODE_LABEL[mode]}; click for ${MODE_LABEL[next]}.`}
        >
            <span className="timestamp__text">{text}</span>
            <span className="timestamp__zone" aria-hidden="true">{zone}</span>
        </button>
    );
};

export default Timestamp;
